'use client'

import { useAuthorization } from '@entities/authentication'
import { getKrPaymentOrder } from '@entities/kr-payment/api'
import { isKrOrderRetired, isKrProviderCancelled } from '@entities/kr-payment/model'
import { usePaymentErrorStaticModel } from '@entities/payment'
import { CheckPaymentStatusByPollingResponse, usePaymentRedirectedSearchParams } from '@features/payment'
import { toCaseCompatible } from '@server/shared/utils/case-adapter'
import { authenticatedFetch } from '@shared/apis/authenticated-fetch'
import { env } from '@shared/config/env'
import { useTranslation } from '@shared/config/i18n'
import { isPollingInProgressState, PollingState, usePolling } from '@shared/hooks'
import { openAlertDialog } from '@shared/ui'
import { redirect, useSearchParams } from 'next/navigation'
import { useCallback, useState } from 'react'

const PAYMENT_CONFIG = {
  POLLING_MAX_ATTEMPTS: 10,
  POLLING_INTERVAL: 1000,
} as const

export const useCheckPaymentStatusPolling = (callbackOptions?: {
  onSuccess: (data: Nullable<{ classId?: string; isDuplicatePurchase?: boolean }>) => void
  isFree?: boolean
}) => {
  const redirectedSearchParams = usePaymentRedirectedSearchParams()
  const searchParams = useSearchParams()
  const t = useTranslation()
  const authorization = useAuthorization()
  const { ERROR_MESSAGES } = usePaymentErrorStaticModel()

  const impSuccess = searchParams?.get('imp_success')
  // V1/V2 호환: imp_uid 또는 payment_id로 완료 여부 확인
  const hasV1Params = redirectedSearchParams?.imp_uid && redirectedSearchParams?.merchant_uid
  const hasV2Params = redirectedSearchParams?.payment_id || redirectedSearchParams?.paymentId
  const isCompleteFromWebviewCallback = Boolean((hasV1Params || hasV2Params) && redirectedSearchParams?.complete)

  const [pollingState, setPollingState] = useState<PollingState<{ merchantUid: string; purchaseOrderId?: string }>>(
    () => {
      // imp_success가 false인 경우는 결제 실패이므로 폴링을 시작하지 않음
      if (impSuccess === 'false' || isKrProviderCancelled(redirectedSearchParams)) {
        return { status: 'idle' }
      }

      if (redirectedSearchParams && isCompleteFromWebviewCallback) {
        // V1/V2 호환: merchant_uid 또는 payment_id/paymentId 사용
        const merchantUid =
          redirectedSearchParams.merchant_uid ?? redirectedSearchParams.payment_id ?? redirectedSearchParams.paymentId
        if (!merchantUid) {
          return { status: 'idle' }
        }
        return {
          status: 'in-progress',
          data: { merchantUid, purchaseOrderId: redirectedSearchParams.purchase_order_id },
        }
      }

      return { status: 'idle' }
    },
  )
  const checkPaymentStatusByPolling = useCallback(async () => {
    if (!isPollingInProgressState(pollingState)) {
      throw new Error(ERROR_MESSAGES.IMP_UID_NOT_FOUND)
    }

    const orderId =
      pollingState.data.purchaseOrderId ??
      (pollingState.data.merchantUid.startsWith('KR_P_') ? pollingState.data.merchantUid : null)
    if (orderId) {
      if (orderId !== pollingState.data.merchantUid) {
        throw new Error(ERROR_MESSAGES.PAYMENT_FAILED)
      }
      const order = await getKrPaymentOrder(orderId, authorization.bearerToken)
      if (order.status === 'FAILED' || isKrOrderRetired(order)) {
        throw new Error(ERROR_MESSAGES.PAYMENT_FAILED)
      }
      if (order.status !== 'PAID') {
        return { status: 'pending' as const, extras: null }
      }
    }
    // Existing polling extras include the issued trial class ID needed for the unchanged booking UI.
    const url = new URL(`/api/v1/payment/podo/check/${pollingState.data.merchantUid}`, env.NEXT_PUBLIC_PODO_API_URL)
    if (callbackOptions?.isFree) {
      url.searchParams.set('isFree', 'true')
    }

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authorization.bearerToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.IMP_UID_NOT_FOUND)
    }

    const podoResponseEntity = toCaseCompatible<CheckPaymentStatusByPollingResponse>(await response.json())

    if (podoResponseEntity.data.status === 'failed') {
      throw new Error(ERROR_MESSAGES.PAYMENT_FAILED)
    }

    return { status: podoResponseEntity.data.status, extras: podoResponseEntity.data.extras }
  }, [
    ERROR_MESSAGES.IMP_UID_NOT_FOUND,
    ERROR_MESSAGES.PAYMENT_FAILED,
    authorization.bearerToken,
    pollingState,
    callbackOptions?.isFree,
  ])

  usePolling<{ classId?: string; isDuplicatePurchase?: boolean }>({
    pollingFn: checkPaymentStatusByPolling,
    enabled: pollingState.status === 'in-progress',
    maxAttempts: PAYMENT_CONFIG.POLLING_MAX_ATTEMPTS,
    interval: PAYMENT_CONFIG.POLLING_INTERVAL,
    onSuccess: (extras) => {
      setPollingState({ status: 'idle' })
      callbackOptions?.onSuccess?.(extras)
    },
    maxAttemptError: () => {
      // 폴링 상태를 반드시 idle 로 복구해야 로딩 오버레이가 무한히 남지 않는다.
      setPollingState({ status: 'idle' })
      openAlertDialog({
        title: ERROR_MESSAGES.MAX_ATTEMPTS_EXCEEDED,
        confirmMessage: t('common.actions.confirm'),
        handleConfirm: () => {
          redirect('/app/user/podo/mypage')
        },
      })
    },
    onError: (error) => {
      console.error('POLLING ERROR', error)
      setPollingState({ status: 'idle' })

      openAlertDialog({
        title: callbackOptions?.isFree ? ERROR_MESSAGES.FREE_TRIAL_PAYMENT_FAILED : ERROR_MESSAGES.PAYMENT_FAILED,
      })
    },
  })

  return { pollingState, setPollingState }
}
